import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  BookOpen, 
  ArrowLeft,
  Play,
  Trophy,
  Coins,
  RotateCcw,
  Video,
  Sparkles,
  Check,
  X,
  Lightbulb,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

type GameState = "idle" | "playing" | "finished";

interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

const ALL_QUESTIONS: QuizQuestion[] = [
  { question: "What is the maximum supply of Bitcoin?", options: ["21 million", "100 million", "18.5 million", "Unlimited"], correctIndex: 0, explanation: "Bitcoin has a hard cap of 21 million coins that can ever be mined." },
  { question: "Who created Bitcoin?", options: ["Vitalik Buterin", "Satoshi Nakamoto", "Charlie Lee", "Changpeng Zhao"], correctIndex: 1, explanation: "Satoshi Nakamoto published the Bitcoin whitepaper in 2008." },
  { question: "What does 'HODL' mean in crypto?", options: ["A token name", "Hold On for Dear Life", "A mining technique", "A wallet type"], correctIndex: 1, explanation: "HODL originated from a typo of 'hold' and became a crypto culture term." },
  { question: "What blockchain does Pi Network use?", options: ["Ethereum", "Its own blockchain", "Bitcoin", "Solana"], correctIndex: 1, explanation: "Pi Network runs on its own proprietary blockchain." },
  { question: "What is a 'gas fee'?", options: ["Power cost for mining", "Transaction processing fee", "Exchange fee", "Interest rate"], correctIndex: 1, explanation: "Gas fees are paid to validators for processing transactions on the blockchain." },
  { question: "What is a smart contract?", options: ["A legal document", "Self-executing code on blockchain", "A crypto wallet", "A mining pool"], correctIndex: 1, explanation: "Smart contracts are programs stored on the blockchain that automatically execute when conditions are met." },
  { question: "What is 'DeFi' short for?", options: ["Defined Finance", "Decentralized Finance", "Default Finance", "Definite Finance"], correctIndex: 1, explanation: "DeFi stands for Decentralized Finance — financial services without intermediaries." },
  { question: "What is an NFT?", options: ["New Financial Token", "Non-Fungible Token", "Network Fee Token", "Next Future Token"], correctIndex: 1, explanation: "NFTs are unique digital assets that represent ownership of items like art or collectibles." },
  { question: "What is a blockchain 'block'?", options: ["A ban on transactions", "A group of verified transactions", "A type of crypto", "A wallet address"], correctIndex: 1, explanation: "A block is a batch of transactions that are verified and added to the blockchain." },
  { question: "What is a crypto 'wallet'?", options: ["A physical wallet", "Software to store crypto keys", "A bank account", "An exchange"], correctIndex: 1, explanation: "A crypto wallet stores your private keys which give you access to your cryptocurrency." },
  { question: "What does 'staking' mean?", options: ["Selling crypto", "Locking crypto to earn rewards", "Mining crypto", "Trading crypto"], correctIndex: 1, explanation: "Staking means locking up crypto to help secure the network and earn rewards." },
  { question: "What is a 'hash' in blockchain?", options: ["A type of currency", "A unique digital fingerprint", "A transaction fee", "A mining tool"], correctIndex: 1, explanation: "A hash is a unique fixed-length string generated from input data, used to verify data integrity." },
  { question: "What year was Bitcoin created?", options: ["2005", "2009", "2012", "2015"], correctIndex: 1, explanation: "The Bitcoin network went live on January 3, 2009." },
  { question: "What is a 'private key'?", options: ["Your username", "A secret code to access your crypto", "Your password", "A bank PIN"], correctIndex: 1, explanation: "A private key is the secret code that proves ownership of your cryptocurrency." },
  { question: "What is 'mining' in crypto?", options: ["Digging for coins", "Verifying transactions & earning rewards", "Buying crypto", "Trading crypto"], correctIndex: 1, explanation: "Mining is the process of using computing power to verify transactions and add them to the blockchain." },
  { question: "What is market capitalization?", options: ["Total coins mined", "Price × Total supply", "Daily trading volume", "Exchange listing fee"], correctIndex: 1, explanation: "Market cap = current price × total circulating supply of the cryptocurrency." },
  { question: "What is a 'seed phrase'?", options: ["A type of token", "A backup phrase for your wallet", "A password hint", "An exchange feature"], correctIndex: 1, explanation: "A seed phrase is 12-24 words that can recover your entire crypto wallet." },
  { question: "What is the Pi Network consensus?", options: ["Proof of Work", "Stellar Consensus Protocol", "Proof of Stake", "Delegated PoS"], correctIndex: 1, explanation: "Pi Network uses a variant of the Stellar Consensus Protocol (SCP)." },
  { question: "What is a 'DEX'?", options: ["Digital Exchange", "Decentralized Exchange", "Default Exchange", "Direct Exchange"], correctIndex: 1, explanation: "A DEX lets you trade crypto directly peer-to-peer without a central authority." },
  { question: "What is 'mainnet'?", options: ["A test network", "The live production blockchain", "A social network", "A mining pool"], correctIndex: 1, explanation: "Mainnet is the live, fully operational blockchain where real transactions occur." },
];

const CryptoQuizGame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [gameState, setGameState] = useState<GameState>("idle");
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [earnedATH, setEarnedATH] = useState(0);
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [showAdGate, setShowAdGate] = useState(false);
  const [adGateLoading, setAdGateLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("cryptoQuizHighScore");
    if (saved) setHighScore(parseInt(saved));
  }, []);

  // Check daily play count on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_crypto_quiz_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    if (plays >= 1) {
      setShowAdGate(true);
    }
  }, [user?.id]);

  const handleAdGateWatch = async () => {
    setAdGateLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '', () => {
        setShowAdGate(false);
      });
    } catch (error) {
      console.error('Ad gate error:', error);
    } finally {
      setAdGateLoading(false);
    }
  };

  const incrementPlayCount = () => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_crypto_quiz_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, (plays + 1).toString());
  };

  const startGame = () => {
    incrementPlayCount();
    // Pick 10 random questions
    const shuffled = [...ALL_QUESTIONS].sort(() => Math.random() - 0.5).slice(0, 10);
    setQuestions(shuffled);
    setCurrentIndex(0);
    setScore(0);
    setGameState("playing");
    setSelectedAnswer(null);
    setShowExplanation(false);
    setEarnedATH(0);
    setShowReward(false);
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const handleAnswer = (index: number) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    
    const isCorrect = index === questions[currentIndex].correctIndex;
    if (isCorrect) setScore(s => s + 1);
    
    setShowExplanation(true);
  };

  const nextQuestion = () => {
    if (currentIndex + 1 >= questions.length) {
      endGame();
    } else {
      setCurrentIndex(i => i + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const endGame = () => {
    setGameState("finished");

    let reward = 0.1;
    if (score >= 9) reward = 2;
    else if (score >= 7) reward = 1.2;
    else if (score >= 5) reward = 0.6;
    else if (score >= 3) reward = 0.3;

    setEarnedATH(reward);

    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem("cryptoQuizHighScore", score.toString());
    }

    // Interstitial ad converted to Rewarded Ad
    setTimeout(() => {
      if (user?.id) piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
    }, 2000);
  };

  const watchAdForBonus = async () => {
    if (!user?.id || isWatchingAd) return;
    setIsWatchingAd(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
      setBonusMultiplier(2);
      setAdWatched(true);
      await arthereumProvider.recordGameAdBonus(user.id, "crypto_quiz", earnedATH);
    } catch (error) {
      console.error("Ad failed:", error);
    } finally {
      setIsWatchingAd(false);
    }
  };

  const claimReward = async () => {
    if (!user?.id || isClaimingReward) return;
    const finalReward = parseFloat((earnedATH * bonusMultiplier).toFixed(2));
    setIsClaimingReward(true);
    try {
      await arthereumProvider.awardGameATH(user.id, "crypto_quiz", finalReward, score);
      dispatch(actions.setActualUser({
        ...userData,
        user: { ...user, arthereumBalance: (user.arthereumBalance || 0) + finalReward },
        miningInfos: userData.miningInfos,
      }));
      setShowReward(true);
    } catch (error: any) {
      alert(error.message || "Failed to claim reward");
    } finally {
      setIsClaimingReward(false);
    }
  };

  const playAgain = () => {
    setGameState("idle");
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const finalReward = earnedATH * bonusMultiplier;
  const currentQ = questions[currentIndex];

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      {showAdGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <Video size={32} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-black text-gray-900">Watch Ad to Play Again!</h3>
            <p className="text-sm text-gray-500">Your free daily play has been used. Watch a short ad to unlock another round!</p>
            <button
              onClick={handleAdGateWatch}
              disabled={adGateLoading}
              className="w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg active:scale-95 transition-all"
            >
              {adGateLoading ? 'Loading Ad...' : '🎬 Watch Ad & Play!'}
            </button>
          </div>
        </div>
      )}
      <CommonPageTitle title="Crypto Quiz" padding={null} />

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(PAGE_NAME.GAMES)}
              className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
            >
              <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
            </button>
            <div>
              <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Crypto Quiz 📚
              </h1>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full ${theme === "dark" ? "bg-emerald-900/30 text-emerald-400" : "bg-emerald-100 text-emerald-600"} text-sm font-bold`}>
            🏆 Best: {highScore}/10
          </div>
        </div>

        {/* Game Container */}
        <div className={`
          rounded-3xl p-6 text-center
          ${theme === "dark" 
            ? "bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30" 
            : "bg-gradient-to-br from-emerald-100 to-teal-100 border border-emerald-300"}
        `}>
          <AnimatePresence mode="wait">
            {gameState === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <BookOpen size={64} className="mx-auto text-emerald-400 mb-4" />
                  <h2 className={`text-xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    How to Play
                  </h2>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                    Answer 10 crypto & blockchain trivia questions.
                    <br />Learn while you earn! 🧠
                  </p>
                </div>

                <div className="space-y-2">
                  <p className={`text-xs ${theme === "dark" ? "text-slate-500" : "text-gray-400"}`}>Rewards:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>0-2: 0.1 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>3-4: 0.3 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>5-6: 0.6 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>7-8: 1.2 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-amber-900/50 text-amber-400" : "bg-amber-100 text-amber-600"}`}>9-10: 2 ATH!</span>
                  </div>
                </div>

                <button
                  onClick={startGame}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95"
                >
                  <Play size={24} /> Start Quiz
                </button>
              </motion.div>
            )}

            {gameState === "playing" && currentQ && (
              <motion.div
                key={`q-${currentIndex}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Progress */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-bold ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"}`}>
                    Question {currentIndex + 1}/10
                  </span>
                  <span className={`text-sm font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    Score: {score}
                  </span>
                </div>
                <div className="w-full bg-gray-700/30 rounded-full h-2 mb-4">
                  <div 
                    className="bg-gradient-to-r from-emerald-400 to-teal-400 h-2 rounded-full transition-all"
                    style={{ width: `${((currentIndex + 1) / 10) * 100}%` }}
                  />
                </div>

                {/* Question */}
                <div className={`p-5 rounded-2xl text-left ${theme === "dark" ? "bg-slate-800/50" : "bg-white/70"}`}>
                  <p className={`text-lg font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {currentQ.question}
                  </p>
                </div>

                {/* Options */}
                <div className="space-y-2">
                  {currentQ.options.map((option, i) => {
                    let optionStyle = theme === "dark" 
                      ? "bg-slate-700 hover:bg-slate-600 text-white"
                      : "bg-white hover:bg-gray-100 text-gray-900";

                    if (selectedAnswer !== null) {
                      if (i === currentQ.correctIndex) {
                        optionStyle = "bg-emerald-500 text-white";
                      } else if (i === selectedAnswer && i !== currentQ.correctIndex) {
                        optionStyle = "bg-red-500 text-white";
                      } else {
                        optionStyle = theme === "dark" ? "bg-slate-800 text-slate-500" : "bg-gray-200 text-gray-400";
                      }
                    }

                    return (
                      <motion.button
                        key={i}
                        whileTap={selectedAnswer === null ? { scale: 0.98 } : {}}
                        onClick={() => handleAnswer(i)}
                        disabled={selectedAnswer !== null}
                        className={`
                          w-full p-4 rounded-xl text-left font-semibold text-sm
                          flex items-center gap-3 transition-all
                          ${optionStyle}
                        `}
                      >
                        <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-xs font-black shrink-0">
                          {String.fromCharCode(65 + i)}
                        </span>
                        {option}
                        {selectedAnswer !== null && i === currentQ.correctIndex && (
                          <Check size={18} className="ml-auto shrink-0" />
                        )}
                        {selectedAnswer !== null && i === selectedAnswer && i !== currentQ.correctIndex && (
                          <X size={18} className="ml-auto shrink-0" />
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Explanation */}
                {showExplanation && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl text-left flex items-start gap-3 ${
                      theme === "dark" ? "bg-blue-900/30 border border-blue-500/30" : "bg-blue-50 border border-blue-200"
                    }`}
                  >
                    <Lightbulb size={18} className="text-blue-400 shrink-0 mt-0.5" />
                    <p className={`text-sm ${theme === "dark" ? "text-blue-300" : "text-blue-700"}`}>
                      {currentQ.explanation}
                    </p>
                  </motion.div>
                )}

                {/* Next Button */}
                {selectedAnswer !== null && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={nextQuestion}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm"
                  >
                    {currentIndex + 1 >= questions.length ? "See Results" : "Next Question →"}
                  </motion.button>
                )}
              </motion.div>
            )}

            {gameState === "finished" && (
              <motion.div
                key="finished"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Trophy size={64} className="mx-auto text-emerald-400 mb-4" />
                  <h2 className={`text-2xl font-black mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    Quiz Complete!
                  </h2>
                  <p className={`text-4xl font-black ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"}`}>
                    {score}/10 Correct
                  </p>
                  {score >= 9 && <p className="text-amber-400 text-sm font-bold mt-2">🧠 Crypto Expert!</p>}
                  {score > highScore && score > 0 && (
                    <p className="text-emerald-400 text-sm font-bold mt-1">🎉 New High Score!</p>
                  )}
                </div>

                <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-emerald-900/30 border border-emerald-500/30" : "bg-emerald-100 border border-emerald-300"}`}>
                  <p className={`text-sm ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"} mb-1`}>You earned:</p>
                  <p className={`text-3xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"} flex items-center justify-center gap-2`}>
                    <Coins className="text-yellow-400" /> 
                    {finalReward.toFixed(2)} ATH
                    {bonusMultiplier > 1 && (
                      <span className="text-emerald-400 text-sm ml-1">({bonusMultiplier}x)</span>
                    )}
                  </p>
                </div>

                {!showReward && !adWatched && (
                  <motion.button
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onClick={watchAdForBonus}
                    disabled={isWatchingAd}
                    className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Video size={20} />
                    {isWatchingAd ? "Loading Ad..." : (
                      <>Watch Ad for <span className="text-emerald-300">2X BONUS</span> <Sparkles size={16} className="text-yellow-300" /></>
                    )}
                  </motion.button>
                )}

                {adWatched && !showReward && (
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center text-sm font-bold">
                    ✨ 2X BONUS APPLIED!
                  </div>
                )}

                {!showReward ? (
                  <button
                    onClick={claimReward}
                    disabled={isClaimingReward}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isClaimingReward ? "Claiming..." : `Claim ${finalReward.toFixed(2)} ATH 🎁`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 rounded-xl bg-emerald-500 text-white text-center font-bold">
                      ✅ Reward Claimed!
                    </div>
                    <button
                      onClick={playAgain}
                      className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg transition-all active:scale-95"
                    >
                      <RotateCcw size={20} /> Play Again
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CryptoQuizGame;
